import { useCallback, useState } from "react";

interface AlertModalProps {
  title?: string;
  message: string;
  variant?: "error" | "info";
  onClose: () => void;
}

export const AlertModal: React.FC<AlertModalProps> = ({ title, message, variant = "error", onClose }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
    <div
      className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            variant === "error" ? "bg-red-500/10" : "bg-cyan-500/10"
          }`}
        >
          <svg
            className={`w-5 h-5 ${variant === "error" ? "text-red-400" : "text-cyan-400"}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">{title ?? (variant === "error" ? "Something went wrong" : "Notice")}</h2>
          <p className="text-sm text-gray-400 mt-0.5 whitespace-pre-line">{message}</p>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200 transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

interface AlertState {
  title?: string;
  message: string;
  variant?: "error" | "info";
}

/** Drop-in replacement for window.alert(): renders a modal instead of a native alert box. */
export function useAlertModal() {
  const [alert, setAlert] = useState<AlertState | null>(null);

  const showAlert = useCallback((message: string, opts?: { title?: string; variant?: "error" | "info" }) => {
    setAlert({ message, title: opts?.title, variant: opts?.variant });
  }, []);

  const alertModal = alert ? (
    <AlertModal title={alert.title} message={alert.message} variant={alert.variant} onClose={() => setAlert(null)} />
  ) : null;

  return { alertModal, showAlert };
}
